import React from "react";

/**
 * OperatorTableShell — Phase 2F Super Admin Console
 *
 * A thin layout primitive every operator drill-down table can reuse.
 * Provides:
 *  - card surface
 *  - sticky header row using a CSS grid template (caller defines columns)
 *  - row borders, hover state
 *  - empty + loading + scroll containers
 *
 * The caller defines the column grid (`gridTemplate`) and renders header
 * cells + each row's cells as direct children, so the columns can vary
 * per page while the visual rhythm stays consistent.
 *
 * Below the iPad-portrait breakpoint we let the caller scroll the table
 * horizontally — explicit choice per the Phase 1 UX brief (operator
 * console is desktop-first; horizontal scroll on small screens OK).
 */
export function OperatorTable({
  gridTemplate,
  header,
  children,
  loading,
  empty,
  emptyState,
  className = "",
  "data-testid": testId,
}) {
  return (
    <div
      className={`bg-paper-2 rounded-card shadow-rest overflow-hidden ${className}`}
      data-testid={testId}
    >
      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          {header && (
            <div
              className="grid gap-4 px-4 py-3 bg-paper-sunk border-b border-border/60 data-sku sticky top-0 z-10"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {header}
            </div>
          )}
          {loading ? (
            <div
              className="px-4 py-8 text-center font-mono text-sm text-muted-foreground"
              data-testid="operator-table-loading"
            >
              Loading…
            </div>
          ) : empty ? (
            emptyState
          ) : (
            <div>{children}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * OperatorTableRow — convenience wrapper for one row in OperatorTable.
 */
export function OperatorTableRow({
  gridTemplate,
  children,
  onClick,
  className = "",
  "data-testid": testId,
}) {
  const interactive = !!onClick;
  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={[
        "grid gap-4 px-4 py-3 items-center border-t border-border/40",
        interactive
          ? "cursor-pointer hover:bg-paper-sunk transition-colors focus:outline-none focus-visible:bg-paper-sunk"
          : "",
        className,
      ].join(" ")}
      style={{ gridTemplateColumns: gridTemplate }}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export default OperatorTable;
